

import { Link } from "react-router-dom"
import { ChefHat, Users, Award, Clock, Star, Utensils } from "lucide-react"

const MeetChef = () => {
  return (
    <div className="meetChef pb-3 bg-primaryLight relative overflow-hidden">
      {/* Floating Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 left-10 w-20 h-20 bg-primary/10 rounded-full animate-pulse"></div>
        <div className="absolute top-32 right-20 w-16 h-16 bg-secondary/10 rounded-full animate-bounce delay-300"></div>
        <div className="absolute bottom-20 left-1/4 w-12 h-12 bg-primary/20 rounded-full animate-pulse delay-700"></div>
        <div className="absolute top-1/2 right-10 w-8 h-8 bg-secondary/15 rounded-full animate-bounce delay-1000"></div>
      </div>

      {/* Animated Chef Hat Icons */}
      <div className="absolute inset-0 pointer-events-none">
        <ChefHat className="absolute top-16 right-1/3 w-6 h-6 text-primary/20 animate-bounce delay-500" />
        <ChefHat className="absolute bottom-32 left-1/5 w-8 h-8 text-secondary/20 animate-pulse delay-1200" />
        <Utensils className="absolute top-1/3 left-10 w-5 h-5 text-primary/15 animate-bounce delay-800" />
      </div>

      <div className="container mx-auto lg:px-2 px-4 relative z-10">
        <div className="grid grid-cols-12 gap-8 items-center min-h-[600px]">
          {/* Left Content */}
          <div className="lg:col-span-7 col-span-12">
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 lg:p-12 shadow-2xl border border-white/20 relative overflow-hidden">
              {/* Decorative gradient overlay */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-primary/20 to-transparent rounded-bl-full"></div>

              <div className="relative z-10">
                {/* Header Section */}
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                      <ChefHat className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="w-full h-[2px] bg-gradient-to-r from-primary via-secondary to-transparent"></div>
                    </div>
                  </div>

                  <h1 className="font-bold text-4xl lg:text-5xl text-secondary mb-2 tracking-tight">
                    Meet the <span className="text-primary">Shefs</span>
                  </h1>

                  <h3 className="alexBrush text-2xl lg:text-3xl text-headGray/80 font-light">Our Culinary Experts</h3>
                </div>

                {/* Content */}
                <div className="space-y-6 mb-8">
                  <p className="text-lg text-headGray leading-relaxed">
                    Shefs Cloud works with a team of talented and passionate home Shefs who combine taste, health, and
                    quality to create mouthwatering meals for you. Every Shef is carefully selected to ensure they meet
                    our high standards for hygiene, skill, and dedication.
                  </p>

                  <p className="text-lg text-headGray leading-relaxed">
                    From traditional home recipes to innovative dishes, our experts bring their culinary expertise to
                    your plate. Our team includes logistics specialists, nutritionists, and customer service
                    professionals who work hard to make your experience simple and satisfying.
                  </p>
                </div>

                {/* CTA Button */}
                <Link
                  to="/all-chef"
                  className="inline-flex items-center gap-3 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-white px-8 py-4 rounded-full font-semibold text-lg tracking-wide transition-all duration-300 hover:shadow-xl hover:scale-105 group"
                >
                  <span className="pl-4  !text-white">Discover All Shefs</span>
                  <Users className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
              </div>
            </div>
          </div>

          {/* Right Stats/Features */}
          <div className="lg:col-span-5 col-span-12">
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/30 hover:bg-white/80 transition-all duration-300 hover:scale-105">
                  <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Award className="w-6 h-6 text-primary" />
                  </div>
                  <div className="text-2xl font-bold text-secondary mb-1">50+</div>
                  <div className="text-sm text-headGray">Dishes</div>
                </div>

                <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/30 hover:bg-white/80 transition-all duration-300 hover:scale-105">
                  <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Star className="w-6 h-6 text-secondary" />
                  </div>
                  <div className="text-2xl font-bold text-secondary mb-1">4.9</div>
                  <div className="text-sm text-headGray">Rating</div>
                </div>
              </div>

              {/* Feature Cards */}
              <div className="space-y-4">
                <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-white/30 hover:bg-white/80 transition-all duration-300 group">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Clock className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-secondary mb-1">Fresh & Timely</h4>
                      <p className="text-sm text-headGray">Delivered right on time, every time</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-white/30 hover:bg-white/80 transition-all duration-300 group">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Users className="w-6 h-6 text-secondary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-secondary mb-1">Quality Assured</h4>
                      <p className="text-sm text-headGray">Carefully selected for excellence</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-white/30 hover:bg-white/80 transition-all duration-300 group">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Utensils className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-secondary mb-1">Diverse Cuisine</h4>
                      <p className="text-sm text-headGray">Traditional to innovative dishes</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MeetChef

